<script setup lang="ts">
import type { DateValue } from '@internationalized/date'
import { getLocalTimeZone, today, parseDate } from '@internationalized/date'
import { Calendar } from '~/components/ui/calendar'

interface INewsCalendar {
  months: Record<string, number[]>
  first_date: string | null
  last_date: string | null
}

const route = useRoute()
const router = useRouter()
const { getPathWithLocale } = useLocalizedPath()

const getInitialDate = () => {
  const { year, month, day } = route.params as { year?: string; month?: string; day?: string }

  if (year && month && day) {
    try {
      const dateString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      return parseDate(dateString)
    } catch (error) {
      console.error('Invalid date from params:', { year, month, day })
      console.error(error)
      return today(getLocalTimeZone())
    }
  }

  return today(getLocalTimeZone())
}

const selectedDate = ref(getInitialDate()) as Ref<DateValue>
const placeholder = ref(selectedDate.value)

// Days with news by year and month: one request covers a whole year of month switching
const daysByYear = ref<Record<number, Record<string, number[]>>>({})
const firstDate = ref<DateValue>()
const lastDate = ref<DateValue>()
const loadingYears = new Set<number>()

const applyCalendar = (year: number, calendar: INewsCalendar) => {
  daysByYear.value = { ...daysByYear.value, [year]: calendar.months }

  // The bounds stop the calendar from paging into months without news
  if (calendar.first_date) firstDate.value = parseDate(calendar.first_date)
  if (calendar.last_date) lastDate.value = parseDate(calendar.last_date)
}

// Rendered on the server and passed in the payload, so opening the page sends no request
const initialYear = placeholder.value.year
const { data: initialCalendar } = await useApi<INewsCalendar>(
  `/news/calendar?year=${initialYear}`,
  { key: `news.calendar.${initialYear}` }
)

if (initialCalendar.value) {
  applyCalendar(initialYear, initialCalendar.value)
}

const loadYear = async (year: number) => {
  if (daysByYear.value[year] || loadingYears.has(year)) return

  loadingYears.add(year)

  try {
    applyCalendar(year, await useApiCall<INewsCalendar>(`/news/calendar?year=${year}`))
  } catch {
    // A failed year is not stored, so it is requested again on the next visit
  } finally {
    loadingYears.delete(year)
  }
}

const onPlaceholderUpdate = (newPlaceholder: DateValue) => {
  placeholder.value = newPlaceholder
  loadYear(newPlaceholder.year)
}

const isDateUnavailable = (date: DateValue) => {
  return !daysByYear.value[date.year]?.[date.month]?.includes(date.day)
}

watch(
  selectedDate,
  (newValue: DateValue) => {
    if (!newValue) return
    const dateString = newValue.toString()

    const [year, month, day] = dateString.split('-')
    const formattedMonth = month.padStart(2, '0')
    const formattedDay = day.padStart(2, '0')
    const newPath = `/news/${year}/${formattedMonth}/${formattedDay}`

    router.push(getPathWithLocale(newPath))
  }
)
</script>

<template>
  <Calendar
    v-model="selectedDate"
    :weekday-format="'short'"
    :week-starts-on="1"
    :locale="'ru'"
    :is-date-unavailable="isDateUnavailable"
    :min-value="firstDate"
    :max-value="lastDate"
    :placeholder="placeholder"
    @update:placeholder="onPlaceholderUpdate"
  />
</template>
